import { useEffect, useState } from 'react';
import {
  ROLES,
  ROLE_LABELS,
  type AccountabilityEventKind,
  type Role,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2G.3 — Employee Accountability & Operational Performance.
 *
 * Pure read of the admin-only /api/accountability endpoints: WHO did
 * WHAT, WHERE and WHEN, with expected/actual counts where recorded.
 * Deliberately factual: no scores, grades, rankings or blame labels are
 * computed here (or anywhere) - Admin interprets the facts. Rows whose
 * source record carried no actor show "not recorded" instead of a guess.
 */

export interface EventActorDto {
  id: string | null;
  name: string | null;
  role: string | null;
}

export interface AccountabilityEventDto {
  id: string;
  kind: AccountabilityEventKind;
  kindLabel: string;
  source: string;
  occurredAt: string;
  actor: EventActorDto | null;
  branch: { id: string; code: string } | null;
  transaction: { id: string; number: string } | null;
  reference: string | null;
  detail: Record<string, any>;
}

interface EventsResponse {
  total: number;
  limit: number;
  offset: number;
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  items: AccountabilityEventDto[];
}

interface SummaryRowDto {
  employee: {
    id: string;
    name: string | null;
    username: string | null;
    role: string | null;
    isActive: boolean;
    branch: { id: string; code: string; name: string } | null;
  };
  counts: Record<string, number>;
}

interface SummaryResponse {
  total: number;
  limit: number;
  offset: number;
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  items: SummaryRowDto[];
}

interface UserOptionDto {
  id: string;
  name: string;
  username: string;
  role: Role;
  isActive: boolean;
  branch: { code: string } | null;
}

interface DetailResponse {
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  employee: {
    id: string;
    name: string;
    username: string;
    role: Role;
    isActive: boolean;
    branch: { id: string; code: string; name: string } | null;
    createdAt: string;
  };
  counts: Record<string, number>;
  recentEvents: AccountabilityEventDto[];
  discrepancies: {
    id: string;
    transaction: { id: string; number: string };
    branch: { id: string; code: string } | null;
    stage: string;
    stageLabel: string;
    machine: string | null;
    completedAt: string | null;
    expectedCount: number | null;
    actualCount: number | null;
    difference: number | null;
    note: string | null;
  }[];
}

export interface TimelineResponse {
  timeZone: string;
  transaction: {
    id: string;
    number: string;
    status: string;
    statusLabel: string;
    branch: { id: string; code: string };
    customerName: string;
    itemCount: number;
    createdAt: string;
  };
  items: AccountabilityEventDto[];
}

const PAGE_SIZE = 20;

const KIND_TONE: Record<AccountabilityEventKind, 'slate' | 'indigo' | 'emerald' | 'amber'> = {
  RECEIVED: 'indigo',
  STATUS_CHANGE: 'slate',
  PROCESSING_STARTED: 'amber',
  PROCESSING_COMPLETED: 'emerald',
  RELEASED: 'emerald',
  DELIVERY_ACTION: 'indigo',
  INVENTORY_MOVEMENT: 'slate',
  PURCHASE_RECORDED: 'slate',
  EXPENSE_RECORDED: 'slate',
  RECORD_CHANGE: 'slate',
};

const COUNT_COLUMNS: { key: string; label: string }[] = [
  { key: 'transactionsReceived', label: 'Received' },
  { key: 'processingCompleted', label: 'Processing' },
  { key: 'foldingCompleted', label: 'Folding' },
  { key: 'releases', label: 'Releases' },
  { key: 'deliveryActions', label: 'Delivery' },
  { key: 'inventoryMovements', label: 'Stock moves' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'expenses', label: 'Expenses' },
];

export function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { timeStyle: 'short' });
}

/** Manila calendar date for the "Today" shortcut (server re-parses it as Manila). */
function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
}

const NOT_RECORDED = (
  <span className="italic text-slate-400">not recorded</span>
);

/** Neutral, factual one-line description of what an event recorded. */
export function describeEvent(event: AccountabilityEventDto): string {
  const d = event.detail;
  switch (event.kind) {
    case 'RECEIVED':
      return d.note ? String(d.note) : 'Recorded at receiving';
    case 'STATUS_CHANGE':
      return `${d.fromLabel} → ${d.toLabel}`;
    case 'PROCESSING_STARTED':
      return `${d.stageLabel}${d.machine ? ` · ${d.machine}` : ''} · in progress`;
    case 'PROCESSING_COMPLETED': {
      const counts =
        d.expectedCount !== null && d.actualCount !== null
          ? `expected ${d.expectedCount} · counted ${d.actualCount} · difference ${d.difference}`
          : 'counts not recorded';
      return `${d.stageLabel}${d.machine ? ` · ${d.machine}` : ''} · ${counts}`;
    }
    case 'RELEASED':
      return `${d.method === 'DELIVERY' ? 'Delivery' : 'Pick-up'} · ${d.itemCount} items`;
    case 'DELIVERY_ACTION':
      return `${d.toLabel}${d.assignee ? ` · ${d.assignee}` : ''}`;
    case 'INVENTORY_MOVEMENT':
      return `${d.movementType} ${d.quantity} ${d.unit}${d.itemName ? ` · ${d.itemName}` : ''}`;
    case 'PURCHASE_RECORDED':
      return `${d.number} · ${d.supplier}`;
    case 'EXPENSE_RECORDED':
      return `${d.number} · ${d.category}`;
    case 'RECORD_CHANGE':
      return `${d.action} (${d.entityType})`;
    default:
      return '';
  }
}

/** True when a completed processing step recorded a count difference. */
export function hasDiscrepancy(event: AccountabilityEventDto): boolean {
  return (
    event.kind === 'PROCESSING_COMPLETED' &&
    event.detail.difference !== null &&
    event.detail.difference !== undefined &&
    event.detail.difference !== 0
  );
}

export function ActorCell({ actor }: { actor: EventActorDto | null }) {
  if (!actor || (!actor.name && !actor.role)) return NOT_RECORDED;
  return (
    <span>
      <span className="font-medium text-slate-900">{actor.name ?? 'not recorded'}</span>
      {actor.role && (
        <span className="mt-0.5 block text-xs text-slate-500">
          {(ROLE_LABELS as Record<string, string>)[actor.role] ?? actor.role}
        </span>
      )}
    </span>
  );
}

export function AccountabilityPage() {
  const { branchFilter } = useAuth();

  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [events, setEvents] = useState<EventsResponse | null>(null);
  const [employees, setEmployees] = useState<UserOptionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // View filters — every one is re-resolved server-side on each request.
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState<'' | Role>('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [page, setPage] = useState(0);

  // Read-only employee detail modal.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Transaction stage timeline modal.
  const [timelineRef, setTimelineRef] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const filterParams = () => {
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (employeeFilter) params.set('employee', employeeFilter);
    if (roleFilter) params.set('role', roleFilter);
    if (fromFilter) params.set('from', fromFilter);
    if (toFilter) params.set('to', toFilter);
    return params;
  };

  // Employee dropdown: branch-scoped server-side (admin passes the view filter).
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (roleFilter) params.set('role', roleFilter);
    params.set('includeInactive', 'true');
    void api
      .get<{ users: UserOptionDto[] }>(`/users?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setEmployees(res.users);
      })
      .catch(() => {
        if (!cancelled) setEmployees([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, roleFilter, refreshKey]);

  // Summary + activity feed in one pass.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const summaryParams = filterParams();
    summaryParams.set('limit', '200');
    const eventsParams = filterParams();
    eventsParams.set('limit', String(PAGE_SIZE));
    eventsParams.set('offset', String(page * PAGE_SIZE));

    void Promise.all([
      api.get<SummaryResponse>(`/accountability/summary?${summaryParams.toString()}`),
      api.get<EventsResponse>(`/accountability?${eventsParams.toString()}`),
    ])
      .then(([summaryRes, eventsRes]) => {
        if (cancelled) return;
        setSummary(summaryRes);
        setEvents(eventsRes);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load accountability data.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, employeeFilter, roleFilter, fromFilter, toFilter, page, refreshKey]);

  // Employee detail (fetched only when its modal is open).
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    const params = filterParams();
    void api
      .get<DetailResponse>(`/accountability/employees/${detailId}?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetailError(err instanceof ApiError ? err.message : 'Failed to load the employee.');
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId, branchFilter, employeeFilter, roleFilter, fromFilter, toFilter, refreshKey]);

  // Transaction timeline (fetched only when its modal is open).
  useEffect(() => {
    if (!timelineRef) {
      setTimeline(null);
      setTimelineError(null);
      return;
    }
    let cancelled = false;
    setTimeline(null);
    setTimelineError(null);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    void api
      .get<TimelineResponse>(
        `/accountability/transactions/${encodeURIComponent(timelineRef)}?${params.toString()}`,
      )
      .then((res) => {
        if (!cancelled) setTimeline(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTimelineError(err instanceof ApiError ? err.message : 'Failed to load the timeline.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [timelineRef, branchFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const resetPageAnd = (fn: () => void) => {
    setPage(0);
    fn();
  };

  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const total = events?.total ?? 0;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);
  const hasFilters = Boolean(employeeFilter || roleFilter || fromFilter || toFilter);

  return (
    <section className="space-y-4" aria-label="Employee accountability">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Employee Accountability
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · who did what, where and when — read-only, from records the system
            already keeps.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          These are <span className="font-semibold text-slate-700">facts, not judgments</span> —
          this view never computes scores, rankings or blame; discrepancies may have many causes
          and are reported as recorded. Missing actors are shown as{' '}
          <span className="italic">not recorded</span> instead of being guessed. Historical records
          are immutable; viewing never changes them.
        </p>
      </Card>

      {error && <ErrorBanner message={error} />}

      {/* Filters — branch comes from the header switcher; the rest re-validated server-side. */}
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <label className="flex min-w-48 flex-col gap-1 text-xs font-semibold text-slate-500">
          Employee
          <select
            value={employeeFilter}
            onChange={(e) => resetPageAnd(() => setEmployeeFilter(e.target.value))}
            className={inputCls}
          >
            <option value="">All employees</option>
            {employees.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.username}){user.isActive ? '' : ' — inactive'}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          Role
          <select
            value={roleFilter}
            onChange={(e) =>
              resetPageAnd(() => setRoleFilter(e.target.value as '' | Role))
            }
            className={inputCls}
          >
            <option value="">All roles</option>
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS[role]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          From
          <input
            type="date"
            value={fromFilter}
            onChange={(e) => resetPageAnd(() => setFromFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          To
          <input
            type="date"
            value={toFilter}
            onChange={(e) => resetPageAnd(() => setToFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <Button
          variant="secondary"
          onClick={() =>
            resetPageAnd(() => {
              setFromFilter(manilaToday());
              setToFilter(manilaToday());
            })
          }
        >
          Today
        </Button>
        {hasFilters && (
          <Button
            variant="secondary"
            onClick={() =>
              resetPageAnd(() => {
                setEmployeeFilter('');
                setRoleFilter('');
                setFromFilter('');
                setToFilter('');
              })
            }
          >
            Clear filters
          </Button>
        )}
        <span className="ml-auto text-xs font-medium text-slate-400">
          Dates are Manila business dates (Asia/Manila){summary?.range ? ` · ${summary.range.from ?? '…'} → ${summary.range.to ?? '…'}` : ''}
        </span>
      </Card>

      {/* Per-employee operational counts (event-level facts). */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700">
          Operational summary by employee
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Counts of recorded events in the selected scope — not grades. “Item difference” sums the
          recorded expected/counted differences of that employee’s completed steps.
        </p>
        {loading && !summary ? (
          <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading summary…
          </div>
        ) : summary && summary.items.length === 0 ? (
          <Card className="mt-2 flex flex-col items-center p-6 text-center">
            <Icon name="users" className="h-8 w-8 text-slate-300" />
            <p className="mt-2 font-medium text-slate-700">No employee activity to show.</p>
            <p className="mt-1 text-sm text-slate-500">
              {hasFilters ? 'Clear a filter to see the full picture.' : 'Recorded actions will appear here as work flows through the system.'}
            </p>
          </Card>
        ) : (
          summary && (
            <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3">Employee</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Branch</th>
                    {COUNT_COLUMNS.map((column) => (
                      <th key={column.key} className="px-3 py-3 text-right">
                        {column.label}
                      </th>
                    ))}
                    <th className="px-3 py-3 text-right">Discrepancies</th>
                    <th className="px-3 py-3 text-right">Item difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {summary.items.map((row) => (
                    <tr
                      key={row.employee.id}
                      onClick={() => setDetailId(row.employee.id)}
                      className="cursor-pointer hover:bg-slate-100/70"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">
                          {row.employee.name ?? (
                            <span className="italic text-slate-400">not recorded</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">
                          {row.employee.username ?? row.employee.id}
                          {!row.employee.isActive && (
                            <span className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
                              inactive
                            </span>
                          )}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.employee.role
                          ? (ROLE_LABELS as Record<string, string>)[row.employee.role] ?? row.employee.role
                          : NOT_RECORDED}
                      </td>
                      <td className="px-4 py-3">
                        <Badge>{row.employee.branch?.code ?? 'ALL'}</Badge>
                      </td>
                      {COUNT_COLUMNS.map((column) => (
                        <td key={column.key} className="px-3 py-3 text-right tabular-nums text-slate-700">
                          {row.counts[column.key] ?? 0}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-right tabular-nums">
                        {(row.counts.discrepancyEvents ?? 0) > 0 ? (
                          <Badge tone="amber">{row.counts.discrepancyEvents}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium text-slate-700">
                        {row.counts.netDifference ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Chronological activity feed (newest first, deterministic pages). */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-700">Recorded activity</h2>
          {events && total > 0 && (
            <p className="text-sm text-slate-500">
              Showing {from}–{to} of {total} {total === 1 ? 'event' : 'events'} · newest first
            </p>
          )}
        </div>

        {loading && !events ? (
          <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading activity…
          </div>
        ) : !events || events.items.length === 0 ? (
          <Card className="mt-2 flex flex-col items-center p-6 text-center">
            <Icon name="check" className="h-8 w-8 text-emerald-400" />
            <p className="mt-2 font-medium text-slate-700">No activity matches these filters.</p>
            <p className="mt-1 text-sm text-slate-500">
              {hasFilters ? 'Clear a filter to see more.' : 'Recorded actions will appear here.'}
            </p>
          </Card>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Employee</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Branch</th>
                    <th className="px-4 py-3">Facts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {events.items.map((event) => (
                    <tr key={event.id} className="align-top hover:bg-slate-100/70">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {formatWhen(event.occurredAt)}
                      </td>
                      <td className="px-4 py-3">
                        <ActorCell actor={event.actor} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={KIND_TONE[event.kind] ?? 'slate'}>{event.kindLabel}</Badge>
                        {hasDiscrepancy(event) && (
                          <Badge tone="amber">Recorded item discrepancy</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {event.transaction ? (
                          <button
                            type="button"
                            onClick={() => setTimelineRef(event.transaction!.number)}
                            className="font-semibold text-indigo-600 underline-offset-2 hover:underline"
                            title="Open stage timeline"
                          >
                            {event.transaction.number}
                          </button>
                        ) : event.reference ? (
                          <span className="font-medium text-slate-700">{event.reference}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge>{event.branch?.code ?? '—'}</Badge>
                      </td>
                      <td className="max-w-80 px-4 py-3 text-slate-600">
                        {describeEvent(event)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


            {/* Deterministic pagination */}
            {total > PAGE_SIZE && (
              <div className="mt-3 flex items-center justify-between">
                <Button
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-500">
                  Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </span>
                <Button
                  variant="secondary"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Employee detail: loading / error / read-only detail */}
      {detailId && !detail && !detailError && (
        <Modal title="Employee" onClose={() => setDetailId(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading employee…
          </div>
        </Modal>
      )}
      {detailId && detailError && (
        <Modal title="Employee" onClose={() => setDetailId(null)}>
          <ErrorBanner message={detailError} />
        </Modal>
      )}
      {detailId && detail && (
        <Modal title={detail.employee.name} onClose={() => setDetailId(null)}>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <Badge>
                {(ROLE_LABELS as Record<string, string>)[detail.employee.role] ?? detail.employee.role}
              </Badge>
              <Badge>{detail.employee.branch?.code ?? 'ALL'}</Badge>
              <Badge tone={detail.employee.isActive ? 'emerald' : 'slate'}>
                {detail.employee.isActive ? 'Active' : 'Inactive'}
              </Badge>
              <span className="text-slate-400">@{detail.employee.username}</span>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700">
                Recorded operational counts
              </h3>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {[...COUNT_COLUMNS, { key: 'discrepancyEvents', label: 'Discrepancies' }].map(
                  (column) => (
                    <div key={column.key} className="rounded-xl border border-slate-200 p-3">
                      <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                        {column.label}
                      </p>
                      <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">
                        {detail.counts[column.key] ?? 0}
                      </p>
                    </div>
                  ),
                )}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Net recorded item difference:{' '}
                <span className="font-semibold tabular-nums text-slate-700">
                  {detail.counts.netDifference ?? 0}
                </span>{' '}
                (sum of that employee’s completed-step differences — a fact, not a judgment)
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700">
                Recorded item discrepancies
              </h3>
              {detail.discrepancies.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No item count differences recorded.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {detail.discrepancies.map((row) => (
                    <li
                      key={row.id}
                      className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                    >
                      <p className="font-semibold">
                        {row.transaction.number} · {row.stageLabel}
                        {row.machine ? ` · ${row.machine}` : ''} · {row.branch?.code ?? '—'}
                      </p>
                      <p className="mt-1 tabular-nums">
                        Expected {row.expectedCount ?? 'not recorded'} · counted{' '}
                        {row.actualCount ?? 'not recorded'} · difference{' '}
                        {row.difference ?? 'not recorded'}
                      </p>
                      <p className="mt-1 text-xs text-amber-700">
                        {row.completedAt ? formatWhen(row.completedAt) : 'completion time not recorded'}
                        {row.note ? ` · ${row.note}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700">Recent activity</h3>
              {detail.recentEvents.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No activity in the selected scope.</p>
              ) : (
                <ul className="mt-2 divide-y divide-slate-100">
                  {detail.recentEvents.map((event) => (
                    <li key={event.id} className="py-2 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium text-slate-800">{event.kindLabel}</span>
                        <span className="text-xs text-slate-500">
                          {formatWhen(event.occurredAt)}
                        </span>
                      </div>
                      <p className="mt-0.5 text-slate-600">
                        {event.transaction?.number ?? event.reference ?? ''}
                        {event.transaction || event.reference ? ' · ' : ''}
                        {describeEvent(event)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Transaction stage timeline */}
      {timelineRef && !timeline && !timelineError && (
        <Modal title={`Transaction ${timelineRef}`} onClose={() => setTimelineRef(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading timeline…
          </div>
        </Modal>
      )}
      {timelineRef && timelineError && (
        <Modal title={`Transaction ${timelineRef}`} onClose={() => setTimelineRef(null)}>
          <ErrorBanner message={timelineError} />
        </Modal>
      )}
      {timelineRef && timeline && (
        <Modal title={`Transaction ${timeline.transaction.number}`} onClose={() => setTimelineRef(null)}>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <Badge>{timeline.transaction.branch.code}</Badge>
              <Badge tone="indigo">{timeline.transaction.statusLabel}</Badge>
              <span>{timeline.transaction.customerName}</span>
              <span className="text-slate-400">{timeline.transaction.itemCount} items</span>
            </div>
            <ol className="space-y-3">
              {timeline.items.map((event) => (
                <li key={event.id} className="flex gap-3">
                  <span className="w-20 shrink-0 pt-0.5 text-right text-xs tabular-nums text-slate-400">
                    {formatTime(event.occurredAt)}
                  </span>
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-400" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">
                        {event.kindLabel}
                      </span>
                      <span className="text-sm text-slate-600">
                        <ActorCell actor={event.actor} />
                      </span>
                      {hasDiscrepancy(event) && <Badge tone="amber">Recorded item discrepancy</Badge>}
                    </div>
                    <p className="mt-0.5 text-sm text-slate-600">{describeEvent(event)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Modal>
      )}
    </section>
  );
}
